/**
 * Niches for the "nicho" step ("¿Qué vendes?").
 *
 * They mirror the backend onboarding question 1 (GraphQL `onboarding`, the same list signup v1 sends
 * in `on_boarding_options`). Option ids differ per environment (dev 1–25, prod 61–85) but the `order`
 * is the same, so each niche is matched by `order` and the id is read at runtime.
 *
 * Each niche also gives:
 * - `productKey`: business key for the product examples (`SignUpV2.categories.<key>.*` in es.json).
 * - `suggestions`: the categories offered in the next step, each with suggested subcategories.
 * Labels are inline per language (es / en / pt) so a niche and its suggestions live in one place.
 */

const s = (es, en, pt, subs = []) => ({ label: { es, en, pt }, subs });
const sub = (es, en, pt) => ({ es, en, pt });

export const NICHES = [
    {
        order: 1,
        key: 'moda',
        icon: 'ri-t-shirt-line',
        productKey: 'ropa',
        label: { es: 'Ropa y moda', en: 'Clothing & fashion', pt: 'Roupas e moda' },
        suggestions: [
            s('Ropa de mujer', "Women's clothing", 'Roupa feminina', [
                sub('Blusas', 'Blouses', 'Blusas'),
                sub('Vestidos', 'Dresses', 'Vestidos'),
                sub('Pantalones', 'Pants', 'Calças'),
                sub('Faldas', 'Skirts', 'Saias'),
            ]),
            s('Ropa de hombre', "Men's clothing", 'Roupa masculina', [
                sub('Camisas', 'Shirts', 'Camisas'),
                sub('Camisetas', 'T-shirts', 'Camisetas'),
                sub('Pantalones', 'Pants', 'Calças'),
            ]),
            s('Accesorios', 'Accessories', 'Acessórios', [
                sub('Bolsos', 'Bags', 'Bolsas'),
                sub('Gorras', 'Caps', 'Bonés'),
                sub('Cinturones', 'Belts', 'Cintos'),
            ]),
            s('Ropa deportiva', 'Sportswear', 'Roupa esportiva'),
            s('Ofertas', 'Sale', 'Ofertas'),
        ],
    },
    {
        order: 2,
        key: 'belleza',
        icon: 'ri-sparkling-2-line',
        productKey: 'belleza',
        label: { es: 'Maquillaje y cuidado personal', en: 'Makeup & personal care', pt: 'Maquiagem e cuidados' },
        suggestions: [
            s('Maquillaje', 'Makeup', 'Maquiagem', [
                sub('Rostro', 'Face', 'Rosto'),
                sub('Ojos', 'Eyes', 'Olhos'),
                sub('Labios', 'Lips', 'Lábios'),
            ]),
            s('Cuidado de la piel', 'Skin care', 'Cuidados com a pele', [
                sub('Limpiadores', 'Cleansers', 'Limpeza'),
                sub('Hidratantes', 'Moisturizers', 'Hidratantes'),
                sub('Protector solar', 'Sunscreen', 'Protetor solar'),
            ]),
            s('Cabello', 'Hair', 'Cabelo', [
                sub('Shampoo', 'Shampoo', 'Shampoo'),
                sub('Tratamientos', 'Treatments', 'Tratamentos'),
            ]),
            s('Perfumes', 'Fragrances', 'Perfumes'),
            s('Uñas', 'Nails', 'Unhas'),
        ],
    },
    {
        order: 3,
        key: 'calzado',
        icon: 'ri-footprint-line',
        productKey: 'ropa',
        label: { es: 'Calzado', en: 'Footwear', pt: 'Calçados' },
        suggestions: [
            s('Mujer', 'Women', 'Feminino', [
                sub('Tenis', 'Sneakers', 'Tênis'),
                sub('Sandalias', 'Sandals', 'Sandálias'),
                sub('Tacones', 'Heels', 'Saltos'),
            ]),
            s('Hombre', 'Men', 'Masculino', [
                sub('Tenis', 'Sneakers', 'Tênis'),
                sub('Formal', 'Dress shoes', 'Social'),
                sub('Botas', 'Boots', 'Botas'),
            ]),
            s('Niños', 'Kids', 'Infantil'),
            s('Deportivo', 'Sports', 'Esportivo'),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 4,
        key: 'hogar',
        icon: 'ri-home-4-line',
        productKey: 'hogar',
        label: { es: 'Hogar y decoración', en: 'Home & decor', pt: 'Casa e decoração' },
        suggestions: [
            s('Cocina', 'Kitchen', 'Cozinha', [
                sub('Utensilios', 'Utensils', 'Utensílios'),
                sub('Ollas y sartenes', 'Pots & pans', 'Panelas'),
            ]),
            s('Electrodomésticos', 'Appliances', 'Eletrodomésticos'),
            s('Decoración', 'Decor', 'Decoração', [
                sub('Cuadros', 'Wall art', 'Quadros'),
                sub('Velas', 'Candles', 'Velas'),
                sub('Plantas', 'Plants', 'Plantas'),
            ]),
            s('Organización', 'Storage', 'Organização'),
            s('Textiles', 'Textiles', 'Têxteis'),
        ],
    },
    {
        order: 5,
        key: 'alimentos',
        icon: 'ri-restaurant-2-line',
        productKey: 'comida',
        label: { es: 'Alimentos', en: 'Food', pt: 'Alimentos' },
        suggestions: [
            s('Platos principales', 'Main dishes', 'Pratos principais'),
            s('Bebidas', 'Drinks', 'Bebidas', [
                sub('Jugos', 'Juices', 'Sucos'),
                sub('Gaseosas', 'Sodas', 'Refrigerantes'),
                sub('Calientes', 'Hot drinks', 'Bebidas quentes'),
            ]),
            s('Postres', 'Desserts', 'Sobremesas'),
            s('Snacks', 'Snacks', 'Lanches'),
            s('Combos', 'Combos', 'Combos'),
        ],
    },
    {
        order: 6,
        key: 'tecnologia',
        icon: 'ri-smartphone-line',
        productKey: 'tecnologia',
        label: { es: 'Tecnología', en: 'Technology', pt: 'Tecnologia' },
        suggestions: [
            s('Celulares', 'Phones', 'Celulares'),
            s('Computadores', 'Computers', 'Computadores'),
            s('Accesorios', 'Accessories', 'Acessórios', [
                sub('Cargadores', 'Chargers', 'Carregadores'),
                sub('Fundas', 'Cases', 'Capinhas'),
                sub('Cables', 'Cables', 'Cabos'),
            ]),
            s('Audio', 'Audio', 'Áudio', [
                sub('Audífonos', 'Headphones', 'Fones de ouvido'),
                sub('Parlantes', 'Speakers', 'Caixas de som'),
            ]),
            s('Gaming', 'Gaming', 'Games'),
        ],
    },
    {
        order: 7,
        key: 'servicios',
        icon: 'ri-customer-service-2-line',
        productKey: 'otro',
        label: { es: 'Servicios', en: 'Services', pt: 'Serviços' },
        suggestions: [
            s('Servicios', 'Services', 'Serviços'),
            s('Paquetes', 'Packages', 'Pacotes'),
            s('Planes', 'Plans', 'Planos'),
            s('Asesorías', 'Consulting', 'Consultorias'),
        ],
    },
    {
        order: 8,
        key: 'juguetes',
        icon: 'ri-gift-line',
        productKey: 'papeleria',
        label: { es: 'Juguetes y regalos', en: 'Toys & gifts', pt: 'Brinquedos e presentes' },
        suggestions: [
            s('Juguetes', 'Toys', 'Brinquedos', [
                sub('Didácticos', 'Educational', 'Educativos'),
                sub('Peluches', 'Plush toys', 'Pelúcias'),
                sub('Juegos de mesa', 'Board games', 'Jogos de tabuleiro'),
            ]),
            s('Regalos', 'Gifts', 'Presentes'),
            s('Detalles', 'Small gifts', 'Lembrancinhas'),
            s('Fiestas', 'Party', 'Festas'),
        ],
    },
    {
        order: 9,
        key: 'vehiculos',
        icon: 'ri-motorbike-line',
        productKey: 'otro',
        label: { es: 'Autos, bicis y motos', en: 'Cars, bikes & motorcycles', pt: 'Carros, bikes e motos' },
        suggestions: [
            s('Repuestos', 'Spare parts', 'Peças'),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Bicicletas', 'Bicycles', 'Bicicletas'),
            s('Motos', 'Motorcycles', 'Motos'),
            s('Lubricantes', 'Lubricants', 'Lubrificantes'),
        ],
    },
    {
        order: 10,
        key: 'mascotas',
        icon: 'ri-bear-smile-line',
        productKey: 'mascotas',
        label: { es: 'Mascotas', en: 'Pets', pt: 'Pets' },
        suggestions: [
            s('Perros', 'Dogs', 'Cachorros', [
                sub('Alimento', 'Food', 'Ração'),
                sub('Juguetes', 'Toys', 'Brinquedos'),
                sub('Camas', 'Beds', 'Caminhas'),
            ]),
            s('Gatos', 'Cats', 'Gatos', [
                sub('Alimento', 'Food', 'Ração'),
                sub('Arena', 'Litter', 'Areia'),
                sub('Juguetes', 'Toys', 'Brinquedos'),
            ]),
            s('Accesorios', 'Accessories', 'Acessórios'),
            s('Higiene', 'Grooming', 'Higiene'),
        ],
    },
    {
        order: 11,
        key: 'cursos',
        icon: 'ri-graduation-cap-line',
        productKey: 'otro',
        label: { es: 'Cursos y educación', en: 'Courses & education', pt: 'Cursos e educação' },
        suggestions: [
            s('Cursos', 'Courses', 'Cursos'),
            s('Talleres', 'Workshops', 'Oficinas'),
            s('Mentorías', 'Mentoring', 'Mentorias'),
            s('Material digital', 'Digital content', 'Material digital'),
        ],
    },
    {
        order: 12,
        key: 'farmacia',
        icon: 'ri-capsule-line',
        productKey: 'salud',
        label: { es: 'Farmacia y salud', en: 'Pharmacy & health', pt: 'Farmácia e saúde' },
        suggestions: [
            s('Medicamentos', 'Medicines', 'Medicamentos'),
            s('Cuidado personal', 'Personal care', 'Cuidados pessoais'),
            s('Vitaminas', 'Vitamins', 'Vitaminas'),
            s('Primeros auxilios', 'First aid', 'Primeiros socorros'),
            s('Bebés', 'Baby', 'Bebês'),
        ],
    },
    {
        order: 13,
        key: 'turismo',
        icon: 'ri-hotel-line',
        productKey: 'otro',
        label: { es: 'Turismo y alojamiento', en: 'Travel & lodging', pt: 'Turismo e hospedagem' },
        suggestions: [
            s('Habitaciones', 'Rooms', 'Quartos'),
            s('Planes', 'Packages', 'Pacotes'),
            s('Tours', 'Tours', 'Passeios'),
            s('Transporte', 'Transport', 'Transporte'),
        ],
    },
    {
        order: 14,
        key: 'floristeria',
        icon: 'ri-flower-line',
        productKey: 'papeleria',
        label: { es: 'Floristería', en: 'Florist', pt: 'Floricultura' },
        suggestions: [
            s('Ramos', 'Bouquets', 'Buquês'),
            s('Arreglos', 'Arrangements', 'Arranjos'),
            s('Plantas', 'Plants', 'Plantas'),
            s('Eventos', 'Events', 'Eventos'),
            s('Detalles', 'Small gifts', 'Lembrancinhas'),
        ],
    },
    {
        order: 15,
        key: 'marketing',
        icon: 'ri-megaphone-line',
        productKey: 'otro',
        label: { es: 'Marketing y publicidad', en: 'Marketing & advertising', pt: 'Marketing e publicidade' },
        suggestions: [
            s('Redes sociales', 'Social media', 'Redes sociais'),
            s('Diseño', 'Design', 'Design'),
            s('Publicidad', 'Advertising', 'Publicidade'),
            s('Planes', 'Plans', 'Planos'),
        ],
    },
    {
        order: 16,
        key: 'inmuebles',
        icon: 'ri-building-line',
        productKey: 'otro',
        label: { es: 'Bienes raíces', en: 'Real estate', pt: 'Imóveis' },
        suggestions: [
            s('Venta', 'For sale', 'Venda'),
            s('Arriendo', 'For rent', 'Aluguel'),
            s('Casas', 'Houses', 'Casas'),
            s('Apartamentos', 'Apartments', 'Apartamentos'),
            s('Locales', 'Commercial', 'Comerciais'),
        ],
    },
    {
        order: 17,
        key: 'optica',
        icon: 'ri-glasses-line',
        productKey: 'accesorios',
        label: { es: 'Óptica', en: 'Optics', pt: 'Ótica' },
        suggestions: [
            s('Monturas', 'Frames', 'Armações'),
            s('Gafas de sol', 'Sunglasses', 'Óculos de sol'),
            s('Lentes de contacto', 'Contact lenses', 'Lentes de contato'),
            s('Accesorios', 'Accessories', 'Acessórios'),
        ],
    },
    {
        order: 18,
        key: 'finanzas',
        icon: 'ri-bank-line',
        productKey: 'otro',
        label: { es: 'Banca y finanzas', en: 'Banking & finance', pt: 'Bancos e finanças' },
        suggestions: [
            s('Créditos', 'Loans', 'Créditos'),
            s('Seguros', 'Insurance', 'Seguros'),
            s('Inversiones', 'Investments', 'Investimentos'),
            s('Asesorías', 'Consulting', 'Consultorias'),
        ],
    },
    {
        order: 19,
        key: 'sexshop',
        icon: 'ri-heart-3-line',
        productKey: 'otro',
        label: { es: 'Sex shop', en: 'Adult store', pt: 'Sex shop' },
        suggestions: [
            s('Juguetes', 'Toys', 'Brinquedos'),
            s('Lencería', 'Lingerie', 'Lingerie'),
            s('Lubricantes', 'Lubricants', 'Lubrificantes'),
            s('Kits', 'Kits', 'Kits'),
        ],
    },
    {
        order: 20,
        key: 'licores',
        icon: 'ri-goblet-line',
        productKey: 'comida',
        label: { es: 'Licores', en: 'Liquor', pt: 'Bebidas alcoólicas' },
        suggestions: [
            s('Cervezas', 'Beer', 'Cervejas'),
            s('Vinos', 'Wine', 'Vinhos'),
            s('Whisky', 'Whisky', 'Uísque'),
            s('Ron', 'Rum', 'Rum'),
            s('Combos', 'Combos', 'Combos'),
        ],
    },
    {
        order: 21,
        key: 'articulos-hogar',
        icon: 'ri-home-smile-line',
        productKey: 'hogar',
        label: { es: 'Artículos de hogar', en: 'Household items', pt: 'Utilidades domésticas' },
        suggestions: [
            s('Cocina', 'Kitchen', 'Cozinha'),
            s('Baño', 'Bathroom', 'Banheiro'),
            s('Limpieza', 'Cleaning', 'Limpeza'),
            s('Organización', 'Storage', 'Organização'),
            s('Decoración', 'Decor', 'Decoração'),
        ],
    },
    {
        order: 22,
        key: 'deportes',
        icon: 'ri-basketball-line',
        productKey: 'ropa',
        label: { es: 'Artículos deportivos', en: 'Sporting goods', pt: 'Artigos esportivos' },
        suggestions: [
            s('Ropa deportiva', 'Sportswear', 'Roupa esportiva'),
            s('Calzado deportivo', 'Sports shoes', 'Calçados esportivos'),
            s('Implementos', 'Equipment', 'Equipamentos'),
            s('Suplementos', 'Supplements', 'Suplementos'),
        ],
    },
    {
        order: 23,
        key: 'libreria',
        icon: 'ri-book-open-line',
        productKey: 'papeleria',
        label: { es: 'Librería y papelería', en: 'Books & stationery', pt: 'Livraria e papelaria' },
        suggestions: [
            s('Libros', 'Books', 'Livros'),
            s('Papelería', 'Stationery', 'Papelaria'),
            s('Útiles escolares', 'School supplies', 'Material escolar'),
            s('Arte', 'Art supplies', 'Arte'),
        ],
    },
    {
        order: 24,
        key: 'construccion',
        icon: 'ri-hammer-line',
        productKey: 'otro',
        label: { es: 'Construcción y ferretería', en: 'Construction & hardware', pt: 'Construção e ferragens' },
        suggestions: [
            s('Herramientas', 'Tools', 'Ferramentas'),
            s('Materiales', 'Materials', 'Materiais'),
            s('Maquinaria', 'Machinery', 'Máquinas'),
            s('Ferretería', 'Hardware', 'Ferragens'),
        ],
    },
    {
        order: 25,
        key: 'otro',
        icon: 'ri-more-line',
        productKey: 'otro',
        label: { es: 'Otro', en: 'Other', pt: 'Outro' },
        suggestions: [],
    },
];

/** How many niches show before "Ver más". */
export const NICHE_PRIMARY_COUNT = 8;

/** How many suggestions are added to "Tus categorías" when the categories step opens. */
export const NICHE_SEED_COUNT = 3;

export const getNiche = (key) => NICHES.find((niche) => niche.key === key) || null;

/** Text in the merchant's language (falls back to Spanish). */
export const localized = (text, language) => (text && (text[language] || text.es)) || '';

/** Accent / case insensitive match for the search box. */
const normalize = (text) =>
    String(text || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();

export const searchNiches = (query, language) => {
    const q = normalize(query).trim();
    if (!q) return NICHES;
    return NICHES.filter((niche) => normalize(localized(niche.label, language)).includes(q));
};

/**
 * Categories (with ids) to offer for a niche in the categories step. Ids are stable per niche so
 * the step can tell a suggestion that is already in "Tus categorías".
 */
export const nicheSuggestions = (key, language) => {
    const niche = getNiche(key);
    if (!niche) return [];
    return niche.suggestions.map((suggestion, index) => ({
        id: `niche-${niche.key}-${index}`,
        label: localized(suggestion.label, language),
        icon: niche.icon,
        subs: suggestion.subs.map((subLabel) => localized(subLabel, language)),
    }));
};

/** Backend option id (this environment) for each niche order: { [order]: id }. */
export const nicheOptionIds = (onboardingQuestions) => {
    const question = (onboardingQuestions || []).find((q) => String(q.id) === '1') || (onboardingQuestions || [])[0];
    const ids = {};
    ((question && question.onboardingOptions) || []).forEach((option) => {
        const id = Number(option.id);
        if (option.order != null && Number.isFinite(id)) ids[option.order] = id;
    });
    return ids;
};

// Loaded once per api and shared by the niche step and the signup (WeakMap: demo and real apis apart).
const optionIdsByApi = new WeakMap();

/** Backend option ids of this environment, `{ [order]: id }` (`{}` when they can't be read). */
export const loadNicheOptionIds = (api) => {
    if (!optionIdsByApi.has(api)) {
        const request = api
            .getOnboardingQuestions()
            .then(nicheOptionIds)
            .catch(() => {
                optionIdsByApi.delete(api); // let the next call retry
                return {};
            });
        optionIdsByApi.set(api, request);
    }
    return optionIdsByApi.get(api);
};

/** Same, without waiting more than `ms` (a slow request never blocks the merchant). */
export const nicheOptionIdsWithin = (api, ms) =>
    Promise.race([loadNicheOptionIds(api), new Promise((resolve) => setTimeout(() => resolve({}), ms))]);
